<template>
	<div
		v-if="
			(currentPackConfig && currentPackConfig.length > 0) ||
			isDisplayPlaceholders
		"
		class="d-flex flex-row nowrap body align-items-md-start mb-2 mb-md-3 mb-lg-2 text-start justify-content-md-start"
	>
		<template
			v-if="
				currentPackConfig &&
				currentPackConfig.length > 0 &&
				!isDisplayPlaceholders
			"
		>
			<div
				class="d-flex flex-wrap justify-content-between justify-content-md-start"
				:class="!isPierreVacance && 'flex-md-column'"
			>
				<div
					v-for="(optionName, option_i) in currentPackConfig"
					:key="option_i"
					class="me-2 me-md-3 text-nowrap mb-2 mb-md-1 d-flex flex-row nowrap align-items-center"
				>
					<!-- LOGO DESKTOP -->
					<div class="me-2 d-md-flex d-none align-items-center">
						<svg :height="iconSize" :width="iconSize" class="icon">
							<use :xlink:href="`#___icon___${optionName}`"></use>
						</svg>
					</div>
					<!-- LOGO MOPBILE -->
					<div class="me-2 d-flex d-md-none align-items-center">
						<svg
							:height="iconSize"
							:width="iconSize"
							class="icon text-primary-blue"
						>
							<use :xlink:href="`#___icon___${optionName}`"></use>
						</svg>
					</div>
					<span class="d-flex align-items-center gap-1 position-relative">
						{{ getLabel(optionName) }}
						<template v-if="optionName === 'night'">
							<svg class="icon info-icon tf-icon-10 tf-icon-sm-20">
								<use xlink:href="#___icon___info"></use>
							</svg>
							<PackageDetalisPopUp :top="'24px'" :left="'0'">
								{{ content.planningSummary_options_night_info }}
							</PackageDetalisPopUp>
						</template>
						<span class="caption text-grey-700 d-flex align-items-center">
						<template v-if="optionName === 'train'">
							{{ trainByLabel }}
							{{ `(${participantsLength_adults}` }}
							<svg
								:height="iconSize_adult"
								:width="iconSize_adult"
								class="icon"
								style="margin-bottom: 2px"
							>
								<use xlink:href="#___icon___person"></use>
							</svg>
							<template v-if="participantsLength_children > 0">
								{{ "+" }}
								{{ participantsLength_children }}
								<svg
									:height="iconSize_child"
									:width="iconSize_child"
									class="icon"
								>
									<use xlink:href="#___icon___person"></use>
								</svg>
							</template>
							{{ `)` }}
						</template>
						<!-- Only show participants and parenthesis if not 'night' or 'home' (en_US) -->
						<template v-else-if="optionName !== 'night' && !(optionName === 'home' && showHomeAsNights)">
							{{ `(${participantsLength_adults}` }}
							<svg
								:height="iconSize_adult"
								:width="iconSize_adult"
								class="icon"
								style="margin-bottom: 2px"
							>
								<use xlink:href="#___icon___person"></use>
							</svg>
							<template v-if="participantsLength_children > 0">
								{{ "+" }}
								{{ participantsLength_children }}
								<svg
									:height="iconSize_child"
									:width="iconSize_child"
									class="icon"
								>
									<use xlink:href="#___icon___person"></use>
								</svg>
							</template>
							<!-- Default: show skipass or service duration and days -->
							<template v-if="optionName !== 'transport' && optionName !== 'flight'">
								<span>
									{{ `, ${variant.skipass} ${content.days})` }}
								</span>
							</template>
							<template v-else>
								{{ `)` }}
							</template>
						</template>
						<!-- Eurostar home: show nights in resort (7, or 7 of a 7+1 stay) -->
						<template v-else-if="optionName === 'home' && showHomeAsNights">
							{{ `(${Math.min(Number(variant.skipass) + (packCode.endsWith('_7') ? 1 : 0), 7) || ''} ${content.planner_nights})` }}
						</template>
						<!-- For 'night' show nothing extra in caption -->
						<template v-else></template>
						</span>
					</span>
				</div>
			</div>
		</template>
		<template v-if="isDisplayPlaceholders">
			<PlaceholderBox :minHeight="'25px'" />
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import PlaceholderBox from '@/vue/planning/components/common/placeholderBox.vue'
import PackageDetalisPopUp from '@/vue/planning/components/result/prestation/packageDetalisPopUp.vue'
import iconSizes from '@/config/iconSizes'
import packOptionsConfig from '@/config/packOptionsConfig'
import { isEurostarOrigin, isNativeTnePack, isNlWebsite, isTneOrigin, isUkWebsite, resolveDisplayedTrainPackCode, variantBelongsToPackCode } from '@/config/trainPackageConfig'
// import getServiceDuration from '@/vue/planning/logic/getServiceDuration'

interface Variant {
	codePackage?: string
	skipass?: string | number
}

interface Props {
	isDisplayPlaceholders?: boolean
	isPierreVacance?: boolean
	variant?: Variant
}

const props = withDefaults(defineProps<Props>(), {
	isDisplayPlaceholders: false,
	isPierreVacance: false,
	variant: () => ({}),
})

const store = useStore()

const content = computed(() => store.state.content)
const participants = computed(() => store.state.participants as number[])
const flightPackCodeSelected = computed(() => store.getters.flightPackCodeSelected)
const showHomeAsNights = computed(() => isUkWebsite() || isNlWebsite())

const packCode = computed(() => props.variant?.codePackage ?? '')

const TNE_TRAIN_PACKS = ['PFTTF', 'PFPTTF', 'PFTTUK', 'PFPTTUK', 'PFTTNL', 'PFPTTNL', 'HTTF']

const isTneTrain = computed(() => {
	const origin = (store.state.flightOrigin as string | undefined)?.split('-')[0] || ''
	if (isTneOrigin(origin)) return true
	if (isEurostarOrigin(origin)) return false
	const codes = [packCode.value, store.state.packCode || '']
	if (codes.some((code) => isNativeTnePack(code))) return true
	return codes.some((code) =>
		TNE_TRAIN_PACKS.some((tnePack) => variantBelongsToPackCode(code, tnePack))
	)
})

const trainByLabel = computed(() => {
	const by = content.value?.planningSummary_options_train_by || ''
	const brand = isTneTrain.value
		? content.value?.planning_tne?.name
		: content.value?.planning_eurostar?.name
	return [by, brand].filter(Boolean).join(' ')
})

const iconSize = iconSizes.l
const iconSize_adult = iconSizes.l
const iconSize_child = iconSizes.s

const currentPackConfig = computed(() => {
	const exactConfig = resolveDisplayedTrainPackCode(packCode.value, props.variant?.skipass) 
	return (packOptionsConfig as Record<string, string[]>)[exactConfig] || []
})

const participantsLength_adults = computed(() => {
	return participants.value.filter((age) => age >= 18).length
})

const participantsLength_children = computed(() => {
	return participants.value.filter((age) => age < 18 && age > 1).length
})

function getLabel(optionName: string): string {
	let contentVariable = `planningSummary_options_${optionName}`
	if (optionName === 'transport' && flightPackCodeSelected.value) {
		contentVariable += '_flight'
	}
	return content.value[contentVariable]
}

watch(
	() => props.variant?.codePackage,
	() => {
		// Package change handling if needed in the future
	},
	{ immediate: true }
)
</script>

<style scoped>
.info-icon:hover + .package-info-container {
	visibility: visible;
}
</style>
